/**
 * Navigation entry names.
 *
 * The built-in translations are the default, but an admin can rename any entry in
 * Settings → Menu. Every place that prints a menu name reads it through here so a rename
 * lands in the navbar, the mobile menu, the bottom bar and the page itself at once.
 */
import { useAppStore } from '@/lib/store';
import { useAdminStore } from '@/lib/admin-store';
import type { BilingualText } from '@/types';

export const MENU_KEYS = [
  'home', 'shop', 'artists', 'exhibitions', 'artFairs', 'dropshop',
  'news', 'contact', 'team', 'faq', 'partners', 'terms',
] as const;

export type MenuKey = (typeof MENU_KEYS)[number];

/** Resolve one custom name against the visitor's language, ignoring blank entries. */
export function resolveMenuLabel(
  labels: Partial<Record<MenuKey, BilingualText>> | undefined,
  key: MenuKey,
  language: 'en' | 'ka',
  fallback: string,
): string {
  const custom = labels?.[key];
  // An empty string means "no custom name" — an admin clearing the field must get the
  // built-in label back rather than a nameless menu entry.
  const label = custom?.[language];
  const english = custom?.en;
  return (typeof label === 'string' && label.trim()) || (typeof english === 'string' && english.trim()) || fallback;
}

/** Hook form: `const menuLabel = useMenuLabel(); menuLabel('dropshop', t.nav.dropshop)`. */
export function useMenuLabel(): (key: MenuKey, fallback: string) => string {
  const language = useAppStore((s) => s.language);
  const labels = useAdminStore((s) => s.settings?.menuLabels);
  return (key, fallback) => resolveMenuLabel(labels, key, language, fallback);
}
